import { AvatarBlock, Button, Heading, Spacer, Steps } from '@ksyos/design-system';
import { useBlocker, useLocation, useNavigate, useParams } from '@ksyos/router';
import { intersection, union } from 'lodash';
import { useEffect, useReducer, useState } from 'react';
import type { EpisodeCategory } from '@common/enums';
import type { IServicesByServiceGroupForEpisodeCategory } from '@common/interfaces/servicesByServiceGroupForEpisodeCategory';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ManagerAuthorization } from '@common/enums';
import { addCarePathServiceSettingsSchema } from '@common/validation/addCarePath';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';
import { canSelectSubsequentServiceForCarePath } from '@frontend/utils/canSelectSubsequentServicesForCarePath';
import { useAbortableFetch } from '@frontend/utils/hooks/';
import { emptyValidationErrors, validate } from '@frontend/utils/validation';
import type { IEmployment } from '../../../interfaces/employment';
import type {
    IOrganizationAddCarePathState,
    IServiceGroupWithProfiles,
} from '../../../interfaces/organizationAddCarePathState';
import type { IProfile } from './steps/AssignRoles';
import { ConfirmationModal } from '../../../elements/modals/ConfirmationModal';
import { RightButtons } from '../../../elements/RightButtons';
import { OverlayTemplate } from '../../../templates/OverlayTemplate';
import { AddCarePathSteps, addCarePathSteps } from './constants/addCarePathSteps';
import { mentalhealthServiceCodes } from './constants/mentalhealthServiceCodes';
import { saveToApi } from './helperFunctions/saveCarePath';
import { OrganizationAddCarePathContext } from './organizationAddCarePathContext';
import { organizationAddCarePathReducer } from './organizationAddCarePathReducer';
import { AssignRoles } from './steps/AssignRoles';
import { ChooseCarePath } from './steps/ChooseCarePath';
import { OrganizationAddCarePathServiceOrganizationSettings } from './steps/OrganizationAddCarePathServiceOrganizationSettings';
import { SuccessAndSummary } from './steps/SuccesAndSummary';

export function OrganizationAddCarePath() {
    const navigate = useNavigate();
    const abortableFetch = useAbortableFetch();
    const { pathname } = useLocation();
    const { organizationId, episodeCategory } = useParams() as {
        episodeCategory: EpisodeCategory;
        organizationId: string;
    };

    const initialState: IOrganizationAddCarePathState = {
        currentStep: AddCarePathSteps.chooseCarePath,
        organizationId,
    };

    const [employments, setEmployments] = useState<IEmployment[]>([]);
    const [serviceGroupMembershipsGroupedByEpisodeCategories, setServiceGroupMembershipsGroupedByEpisodeCategories] =
        useState<IServicesByServiceGroupForEpisodeCategory[]>([]);
    const [addCarePathState, dispatchAddCarePathAction] = useReducer(organizationAddCarePathReducer, initialState);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);

    const [isBlocked, setIsBlocked] = useState(true);
    const blocker = useBlocker(() => isBlocked);

    const defaultReturnUrl = `/organizations/${organizationId}/view${
        episodeCategory ? `/carePaths/${episodeCategory}` : ''
    }`;
    assertHasAuthorization(ManagerAuthorization.organizationAddEdit);

    useEffect(() => {
        loadAvailableCarePathsWithServiceGroups();
    }, []);

    useEffect(() => {
        loadOrganization();
        loadEmployments();
    }, [organizationId]);

    if (!addCarePathState.organization) {
        return null;
    }

    return (
        <OrganizationAddCarePathContext.Provider value={{ addCarePathState, dispatchAddCarePathAction }}>
            {pathname.includes('success') ? (
                <OverlayTemplate>
                    <SuccessAndSummary organizationName={addCarePathState.organization.name} />
                </OverlayTemplate>
            ) : (
                <>
                    <OverlayTemplate
                        leftButtons={
                            <Button
                                onClick={() => navigate(defaultReturnUrl)}
                                variant="outline"
                            >
                                {translateKey('common.cancelling')}
                            </Button>
                        }
                        rightButtons={
                            <RightButtons
                                currentStep={addCarePathState.currentStep}
                                isDisabled={additionalCarePathSettingsRequired()}
                                nextStepLabel={
                                    addCarePathState.currentStep === AddCarePathSteps.settings
                                        ? translateKey('common.saveButton')
                                        : undefined
                                }
                                onClickNextStep={goToNextStep}
                                onClickPreviousStep={goToPreviousStep}
                            />
                        }
                    >
                        <Spacer spacing="large">
                            <Heading size="xlarge">{getHeader()}</Heading>
                            <AvatarBlock
                                backgroundColor="white"
                                icon="organization"
                                shape="square"
                                size="medium"
                                title={addCarePathState.organization.name}
                            />
                        </Spacer>
                        <Steps
                            activeStep={addCarePathState.currentStep}
                            sx={{ mb: 6 }}
                        >
                            {addCarePathSteps({
                                isEpisodePreselected: Boolean(episodeCategory),
                            }).map((step, index) => (
                                <Steps.Step
                                    key={step.title}
                                    label={step.title}
                                    onClick={() => handleBreadCrumbSteps(index)}
                                />
                            ))}
                        </Steps>

                        {renderSteps()}
                    </OverlayTemplate>

                    <ConfirmationModal
                        handleNo={() => blocker.reset?.()}
                        handleYes={() => {
                            blocker.proceed?.();
                        }}
                        isOpen={blocker.state === 'blocked'}
                        question={translateKey('episodeCategory.confirmCancelMessage')}
                        title={translateKey('episodeCategory.confirmCancelTitle')}
                    />
                </>
            )}
        </OrganizationAddCarePathContext.Provider>
    );

    function handleBreadCrumbSteps(step: number) {
        dispatchAddCarePathAction({
            type: 'setCurrentStep',
            currentStep: step,
        });
    }

    function getHeader() {
        return episodeCategory
            ? translateKey(
                  'episodeCategory.addServiceForEpisodeCategory',
                  translateKey(`episodeCategoryEnum.${episodeCategory}`)
              )
            : translateKey('episodeCategory.addEpisodeCategory');
    }

    function goToPreviousStep() {
        setErrors(emptyValidationErrors);
        dispatchAddCarePathAction({ type: 'setCurrentStep', currentStep: addCarePathState.currentStep - 1 });
    }

    async function goToNextStep() {
        if (await canContinueToNextStep()) {
            if (addCarePathState.currentStep === AddCarePathSteps.chooseCarePath) {
                await loadServiceGroupsProfiles();
            }
            dispatchAddCarePathAction({ type: 'setCurrentStep', currentStep: addCarePathState.currentStep + 1 });
        }
    }

    async function loadProfiles(serviceGroup: IServiceGroupWithProfiles) {
        const serviceGroupIdsQuery = `serviceGroupIds[]=${serviceGroup.id}`;

        const services = union(
            serviceGroup.services?.filter((service) => !mentalhealthServiceCodes.includes(service.code)),
            serviceGroup?.services
        );

        const hasRoleForServiceIdsQuery = services.map((service) => `hasRoleForServiceIds[]=${service!.id}`).join('&');

        const query = [serviceGroupIdsQuery, hasRoleForServiceIdsQuery].join('&');

        const url = `/api/profiles?${query}`;
        const profilesResult = await abortableFetch.fetchJson(url, 'GET');

        return profilesResult.json.items as IProfile[];
    }

    async function getDefaultSettings(profileId: string) {
        const url = `/api/profiles/${profileId}/getDefaultSettings`;

        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        const queries = canSelectSubsequentServiceForCarePath(addCarePathState.episodeCategory?.name!)
            ? '?includeSubsequentServices=true'
            : '';
        const settingsResult = await abortableFetch.fetchJson(`${url}${queries}`, 'GET');

        return settingsResult.json.items;
    }

    async function loadServiceGroupsProfiles() {
        if (!addCarePathState.serviceGroups) {
            return;
        }

        const serviceGroupsWithProfiles = addCarePathState.serviceGroups.filter(
            ({ profiles }) => profiles !== undefined
        );
        const serviceGroupsWithoutProfiles = addCarePathState.serviceGroups.filter(
            ({ profiles }) => profiles === undefined
        );

        const profileSettings = addCarePathState.defaultProfileSettings ?? {};

        for (const serviceGroup of serviceGroupsWithoutProfiles) {
            const profiles = await loadProfiles(serviceGroup);

            for (const profile of profiles) {
                const settings = await getDefaultSettings(profile.id);
                profile.settings = settings;
                if (!profileSettings?.[profile.id]) {
                    profileSettings[profile.id] = { ...settings };
                }
            }

            serviceGroupsWithProfiles.push({ ...serviceGroup, profiles });
        }

        dispatchAddCarePathAction({ type: 'setServiceGroups', serviceGroups: serviceGroupsWithProfiles });
        dispatchAddCarePathAction({ type: 'setDefaultProfileSettings', defaultProfileSettings: profileSettings });
    }

    function renderSteps() {
        if (addCarePathState.currentStep === AddCarePathSteps.chooseCarePath) {
            return (
                <ChooseCarePath
                    episodeCategory={episodeCategory}
                    serviceGroupMembershipsGroupedByEpisodeCategories={
                        serviceGroupMembershipsGroupedByEpisodeCategories
                    }
                />
            );
        }

        if (addCarePathState.currentStep === AddCarePathSteps.assignRoles) {
            return <AssignRoles employments={employments} />;
        }

        if (addCarePathState.currentStep === AddCarePathSteps.settings) {
            return (
                <OrganizationAddCarePathServiceOrganizationSettings
                    employments={employments}
                    errors={errors}
                />
            );
        }
    }

    async function loadAvailableCarePathsWithServiceGroups() {
        const url = `/api/organizations/${addCarePathState.organizationId}/addableServiceGroups${
            episodeCategory ? '?episodeCategory=' + episodeCategory : ''
        }`;
        const result = await abortableFetch.fetchJson(url, 'GET');

        setServiceGroupMembershipsGroupedByEpisodeCategories(result.json.items);
    }

    function additionalCarePathSettingsRequired() {
        if (addCarePathState.currentStep === AddCarePathSteps.chooseCarePath) {
            if (addCarePathState.episodeCategory && addCarePathState.serviceGroups) {
                const serviceGroupMembership = serviceGroupMembershipsGroupedByEpisodeCategories.find(
                    (category) => category.episodeCategory.name === addCarePathState.episodeCategory!.name
                );
                if (serviceGroupMembership) {
                    for (const stateServiceGroup of addCarePathState.serviceGroups) {
                        const serviceGroup = serviceGroupMembership.serviceGroups.find(
                            (sg) => sg.id === stateServiceGroup.id
                        );

                        if (!serviceGroup || !serviceGroup.services) {
                            return false;
                        }

                        const requiredServicesCodes = intersection(
                            mentalhealthServiceCodes,
                            serviceGroup.services.map((service) => service.code)
                        );
                        if (serviceGroup && requiredServicesCodes.length > 0) {
                            const selectedServicesCodes = serviceGroup.services
                                .filter((service) =>
                                    stateServiceGroup.services?.some((stateService) => stateService.id === service.id)
                                )
                                .map((service) => service.code);

                            const selectedServiceCodes = intersection(requiredServicesCodes, selectedServicesCodes);
                            if (selectedServiceCodes.length === 0) {
                                return true;
                            }
                        }
                    }
                }

                return false;
            }

            return true;
        }

        if (addCarePathState.currentStep === AddCarePathSteps.assignRoles) {
            const serviceGroupIds = addCarePathState.serviceGroups!.map((serviceGroup) => serviceGroup.id);
            const serviceGroupIdsWithSelectedEmployments = addCarePathState.roles?.map((role) => role.serviceGroupId);

            return !serviceGroupIds.every((serviceGroupId) =>
                serviceGroupIdsWithSelectedEmployments?.includes(serviceGroupId)
            );
        }

        if (
            addCarePathState.currentStep === AddCarePathSteps.settings &&
            !addCarePathState.serviceOrganizationSettings
        ) {
            return true;
        }

        return false;
    }

    async function canContinueToNextStep() {
        if (addCarePathState.currentStep !== AddCarePathSteps.settings) {
            return true;
        }

        const { errors: validationErrors } = validate(addCarePathServiceSettingsSchema, addCarePathState);
        setErrors(validationErrors);

        if (validationErrors.size > 0) {
            return false;
        }

        const result = await saveToApi(organizationId, addCarePathState);

        if (result === 'success') {
            setIsBlocked(false);
            setTimeout(() => {
                navigate(pathname + '/success');
            });
        } else {
            setErrors(result.errors);

            return false;
        }
    }

    async function loadOrganization() {
        const url = `/api/organizations/${organizationId}`;
        const result = await abortableFetch.fetchJson(url, 'GET');

        dispatchAddCarePathAction({ type: 'setOrganization', organization: result.json });
    }

    async function loadEmployments() {
        const url = `/api/employments/${organizationId}/practitioners?isActive=true&includeProfileThumbnail=true`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setEmployments(result.json.items);
    }
}
